/**
 * AutoCAD PICKBOX sprite — small square at the cursor that replaces
 * the OS cursor in idle mode. Strict parity with AutoCAD's selection
 * pickbox:
 *
 *   - Visible only when `crosshairVisible && idleSelectionMode` (no
 *     active draft command).
 *   - Half-size driven by PICKBOX sysvar (CSS px). Default 3 → 6 px box.
 *   - Border color follows CURSORCOLOR-derived `cursorColorCss`.
 *   - On entity hover (`useStageHoverStore.entityId !== null`) the
 *     border thickens + uses a brighter color, matching AutoCAD's
 *     SELECTIONPREVIEW visual.
 *
 * Subscribes to `useStageHoverStore` via `subscribe()` + local React
 * state for `entityId` so only this leaf re-renders on hover boundary
 * crosses — Scene3DStage and its heavy children are unaffected. Same
 * isolation pattern as `SelectionTextureSync.ts`.
 */

import * as React from "react";
import { useEffect, useState } from "react";
import { useStageHoverStore } from "../../state/hoverState";

interface PickboxCursorProps {
  readonly cursorClient: { x: number; y: number } | null;
  readonly crosshairVisible: boolean;
  readonly idleSelectionMode: boolean;
  readonly halfPx: number;
  readonly cursorColorCss: string | undefined;
}

export function PickboxCursor(
  props: PickboxCursorProps,
): React.ReactElement | null {
  const [hovered, setHovered] = useState<boolean>(
    () => useStageHoverStore.getState().entityId !== null,
  );
  useEffect(() => {
    return useStageHoverStore.subscribe((state, prev) => {
      if (state.entityId === prev.entityId) return;
      setHovered(state.entityId !== null);
    });
  }, []);

  if (!props.crosshairVisible || !props.idleSelectionMode) return null;
  if (!props.cursorClient) return null;

  const half = Math.max(1, Math.floor(props.halfPx));
  const size = half * 2;
  const borderWidth = hovered ? 2 : 1;
  // Hover color: bright cyan (AutoCAD SELECTIONPREVIEW default — color
  // index 251 ≈ #00FFFF). Idle: follow CURSORCOLOR.
  const borderColor = hovered
    ? "rgba(0, 255, 255, 0.9)"
    : props.cursorColorCss ?? "rgba(170, 170, 170, 0.85)";

  return (
    <div
      data-testid="pickbox-cursor"
      style={{
        position: "absolute",
        // With box-sizing:content-box the border is drawn OUTSIDE the
        // width/height. So placing the content box at (x - half, y - half)
        // with width = height = 2*half centers the visible bordered box
        // exactly on the cursor.
        left: props.cursorClient.x - half,
        top: props.cursorClient.y - half,
        width: size,
        height: size,
        border: `${borderWidth}px solid ${borderColor}`,
        boxSizing: "content-box",
        pointerEvents: "none",
        // AutoCAD pickbox has no fill — it's an outline.
        background: "transparent",
        // Render above world overlays but below tooltips.
        zIndex: 5,
      }}
    />
  );
}
